// Interceptor global para manejar tokens expirados automáticamente
import { clearSession } from './session';

class AuthInterceptor {
    constructor() {
        this.isInitialized = false;
        this.originalFetch = null;
        this.refreshPromise = null;
    }

    init() {
        if (this.isInitialized) return;
        
        this.originalFetch = window.fetch;
        this.interceptFetch();
        this.isInitialized = true;
        
        console.log('🔐 Interceptor de autenticación inicializado');
    }

    interceptFetch() {
        const self = this; // Preservar el contexto
        window.fetch = async function(...args) {
            try {
                // Evitar interceptar peticiones de login y refresh
                const [url, options] = args;
                const headers = (options && options.headers) || {};
                const sentToken = typeof headers.get === 'function'
                    ? Boolean(headers.get('Authorization'))
                    : Boolean(headers.Authorization || headers.authorization);
                // Solo tiene sentido renovar la sesion si la peticion iba con token; las de
                // acceso (login, registro, Google) devuelven 401 por otros motivos
                if (!sentToken || (typeof url === 'string' && (
                    url.includes('/login') ||
                    url.includes('/refresh-token') ||
                    url.includes('/register') ||
                    url.includes('/auth/')
                ))) {
                    return await self.originalFetch.apply(this, args);
                }

                // Primera petición
                let response = await self.originalFetch.apply(this, args);
                
                // Si es un error 401 (token expirado), intentar refresh
                if (response.status === 401) {
                    console.log('🔄 Token expirado detectado, intentando refresh...');
                    
                    // Evitar bucles infinitos - solo intentar refresh una vez
                    const isRetry = args[1]?.headers?.['X-Retry-Attempt'];
                    if (isRetry) {
                        console.log('❌ Ya se intentó refresh, redirigiendo al login');
                        self.handleAuthFailure();
                        return response;
                    }
                    
                    const newToken = await self.refreshToken();
                    if (newToken) {
                        // Reintentar la petición original con el nuevo token
                        const [url, options] = args;
                        const newOptions = {
                            ...options,
                            headers: {
                                ...options?.headers,
                                'Authorization': `Bearer ${newToken}`,
                                'X-Retry-Attempt': '1' // Marcar como reintento
                            }
                        };
                        
                        response = await self.originalFetch.apply(this, [url, newOptions]);
                        console.log('✅ Petición reintentada con nuevo token');
                    } else {
                        // Si no se puede refrescar, redirigir al login
                        self.handleAuthFailure();
                    }
                }
                
                return response;
            } catch (error) {
                console.error('❌ Error en interceptor de autenticación:', error);
                return self.originalFetch.apply(this, args);
            }
        };
    }

    async refreshToken() {
        // Evitar múltiples requests de refresh simultáneos
        if (this.refreshPromise) {
            return this.refreshPromise;
        }

        this.refreshPromise = this.performRefresh();
        const result = await this.refreshPromise;
        this.refreshPromise = null;
        return result;
    }

    async performRefresh() {
        try {
            const refreshToken = localStorage.getItem('refreshToken');
            if (!refreshToken) {
                console.log('❌ No hay refresh token disponible');
                return null;
            }

            const response = await this.originalFetch(`${process.env.REACT_APP_API_URL}/refresh-token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${refreshToken}`
                }
            });

            if (response.ok) {
                const { token, refreshToken: newRefreshToken } = await response.json();
                
                // Actualizar tokens en localStorage
                localStorage.setItem('token', token);
                if (newRefreshToken) {
                    localStorage.setItem('refreshToken', newRefreshToken);
                }
                
                console.log('✅ Token refrescado exitosamente');
                return token;
            } else {
                console.log('❌ Fallo al refrescar token');
                return null;
            }
        } catch (error) {
            console.error('❌ Error al refrescar token:', error);
            return null;
        }
    }

    handleAuthFailure() {
        console.log('🚪 Redirigiendo al login por fallo de autenticación');
        
        // Limpiar tokens
        clearSession();
        
        // Despachar evento de cambio de autenticación
        window.dispatchEvent(new Event('authChange'));
        
        // Redirigir al login solo si no estamos ya en una página de autenticación
        const currentPath = window.location.pathname;
        if (!currentPath.includes('/login') && !currentPath.includes('/register') && currentPath !== '/') {
            // Usar setTimeout para evitar problemas de navegación durante el manejo de errores
            setTimeout(() => {
                window.location.href = '/login';
            }, 100);
        }
    }

    // Método para limpiar el interceptor (útil para testing)
    cleanup() {
        if (this.originalFetch) {
            window.fetch = this.originalFetch;
            this.isInitialized = false;
            console.log('🧹 Interceptor de autenticación limpiado');
        }
    }

    // Método para deshabilitar temporalmente el interceptor
    disable() {
        if (this.originalFetch && this.isInitialized) {
            window.fetch = this.originalFetch;
            console.log('⏸️ Interceptor de autenticación deshabilitado temporalmente');
        }
    }

    // Método para reactivar el interceptor
    enable() {
        if (this.originalFetch && this.isInitialized) {
            this.interceptFetch();
            console.log('▶️ Interceptor de autenticación reactivado');
        }
    }
}

// Crear instancia global
const authInterceptor = new AuthInterceptor();

export default authInterceptor;

